import { useState, useEffect, useRef } from "react";
import "./bot.scss";
import ServicesCarousel from "../../components/servicesCarousel";
import PlanListCarousel from "../../components/plan-list-carousel/plan-list-carousel";
import Options from "../../components/options-buttons/options-buttons";
import PaymentModal from "../../components/card-payment/card-payment";
import steps from "./stepModules/steps";
import useBotControlStore from "../../storeModules/useBotControlStore";
import useOnceEffect from "../../utils/hooks/useOnceEffect";
import useConsorcioStore from "../../storeModules/useConsorcioStore";
import useInitialConfigStore from "../../storeModules/useInitialConfigStore";
import findStep from "./stepModules/findStep";
import {
  setLoadingMessage,
  removeLoadingMessage,
} from "../../utils/setLoadingMessage";
import sleepAwait from "../../utils/sleepAwait";
import BotMessages from "./components/botMessages";
import StepButtons from "../../components/StepButtons";
import generateInitialCopys from "./utils/generateInitialCopys";
import handleSetPlansMap from "./utils/handleSetPlansMap";
import handleOptionClick from "./utils/handleOptionClick";
import trackConversacaoStep from "../../utils/trackConversacaoStep";
import {
  isSecondaryCopyStep,
  stepRequiresStructuredTracking,
} from "../../utils/buildStepStructuredItems";
import { OPENING_BLOCK_STEP } from "../../utils/openingBlockConstants";
import { isAutoAdvanceStep } from "../../utils/stepMessageBlocks";
import getStepBotOptions from "../../utils/getStepBotOptions";
import etapaSimulacaoTagHelper from "../../utils/sendTag/etapaSimulacaoTagHelper";
import { iniciarSimulacaoTag } from "../../utils/sendTag/index";

const META_STEPS = ["verifica_retomada", "retomar_conversa"];

const ChatBot = ({ bannerSrc }) => {
  const messagesEndRef = useRef(null);
  const {
    messages,
    setMessages,
    currentStep,
    setCurrentStep,
    isBotLoading,
    showPaymentModal,
    setShowPaymentModal,
  } = useBotControlStore((state) => state);

  const [plansMap, setPlansMap] = useState([]);

  const { fetchedPlans } = useConsorcioStore((state) => state);

  const { fakeLoad, initialCopys } = useInitialConfigStore((state) => state);

  useOnceEffect(() => {
    const { currentStep } = useBotControlStore.getState();
    if (currentStep >= 0) return;

    iniciarSimulacaoTag();
    setCurrentStep(findStep("verifica_retomada").index);
  });

  // Adiciona os copys dinâmicos que vem das configs iniciais n8n
  useEffect(() => {
    generateInitialCopys(initialCopys, steps);
  }, [initialCopys]);

  useEffect(() => {
    handleSetPlansMap(fetchedPlans, setPlansMap);
  }, [fetchedPlans]);

  const getNewMessage = (step, time) => {
    return {
      sender: "bot",
      text: typeof step.text === "function" ? step.text() : step.text,
      time: time,
      component: step.component || null,
      loading: step.loading || null,
      buttons: step.buttons || [],
      name: step.name || null,
      shouldPersistComponent: step.shouldPersistComponent || false,
    };
  };

  const triggerStepActions = (newMessage, step) => {
    const {
      skipStepTracking,
      skipStepAction,
      appendPendingMessageBlock,
      clearPendingMessageBlock,
    } = useBotControlStore.getState();

    const shouldAppendBotMessage =
      (steps[currentStep].text ||
        steps[currentStep].component ||
        steps[currentStep].loading) &&
      !skipStepTracking;

    etapaSimulacaoTagHelper(step.name);

    if (shouldAppendBotMessage) {
      setMessages(newMessage);
    }

    if (step.action && !step.inputType && !skipStepAction) {
      step.action();
    }

    if (!step.inputType && !step.options && step.nextStep) {
      setCurrentStep(step.nextStep);
    }

    const rawText = typeof step.text === "function" ? step.text() : step.text;
    const resolvedText = (rawText ?? "") || step.historyMessage || "";
    const resolvedOptions = getStepBotOptions(step);
    useBotControlStore.setState({
      lastRenderedStep: {
        name: step.name ?? "",
        botMessage: resolvedText,
        botOptions: resolvedOptions,
      },
    });

    const hasTrackableContent = resolvedText || resolvedOptions.length > 0;
    const usesOpeningBlock = step.name === OPENING_BLOCK_STEP;
    const autoAdvance = isAutoAdvanceStep(step);
    const hasStructuredTracking = stepRequiresStructuredTracking(step.name);
    const shouldTrackBot =
      step.name &&
      !META_STEPS.includes(step.name) &&
      !isSecondaryCopyStep(step.name) &&
      hasTrackableContent &&
      !skipStepTracking &&
      !usesOpeningBlock &&
      (!autoAdvance || hasStructuredTracking);

    if (shouldTrackBot && autoAdvance && usesOpeningBlock) {
      clearPendingMessageBlock();
    }

    if (
      !shouldTrackBot &&
      !skipStepTracking &&
      autoAdvance &&
      !usesOpeningBlock &&
      !isSecondaryCopyStep(step.name) &&
      resolvedText
    ) {
      appendPendingMessageBlock({
        step: step.name,
        message: resolvedText,
      });
    }

    if (shouldTrackBot) {
      const preludeBlock = usesOpeningBlock
        ? []
        : useBotControlStore.getState().pendingMessageBlock;

      trackConversacaoStep({
        stepName: step.name,
        botMessage: resolvedText,
        botOptions: resolvedOptions,
        preludeBlock,
      });
      if (!usesOpeningBlock) {
        clearPendingMessageBlock();
      }
    }

    if (skipStepTracking || skipStepAction) {
      useBotControlStore.setState({
        skipStepTracking: false,
        skipStepAction: false,
      });
    }
  };

  useEffect(() => {
    async function handleStep() {
      if (currentStep >= 0 && steps[currentStep]) {
        const shouldFakeLoad = !steps[currentStep].isWaitingForFetch;

        if (shouldFakeLoad) {
          setLoadingMessage();
          await sleepAwait(fakeLoad);
          removeLoadingMessage();
        }

        const step = steps[currentStep];
        const time = new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });

        const newMessage = getNewMessage(step, time);
        triggerStepActions(newMessage, step);
      }
    }
    handleStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const closePaymentModal = () => {
    setShowPaymentModal(false);
  };

  useEffect(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth",
      });
    }, 0);
  }, [messages, currentStep]);

  return (
    <div className="full">
      <div className="container">
        {bannerSrc && !META_STEPS.includes(steps[currentStep]?.name) && (
          <div className="banner">
            <img src={bannerSrc} alt="Icon" />
          </div>
        )}

        <BotMessages messagesEndRef={messagesEndRef} />

        {!isBotLoading && (
          <>
            <PaymentModal
              isVisible={showPaymentModal}
              onClose={closePaymentModal}
            />

            {steps[currentStep]?.useCarousel && (
              <ServicesCarousel onOptionClick={handleOptionClick} />
            )}

            {steps[currentStep]?.useCarouselPlan && (
              <PlanListCarousel
                options={plansMap}
                onOptionClick={handleOptionClick}
              />
            )}

            <div
              className={`options-container ${
                currentStep === findStep("alterar_cpf_cnpj").index ||
                currentStep === findStep("alterar_celular").index ||
                currentStep === findStep("pergunta_nota_especialista").index
                  ? "customButtons"
                  : ""
              }`}
            >
              {!steps[currentStep]?.useCarouselPlan &&
                !steps[currentStep]?.useCarousel &&
                steps[currentStep]?.options && (
                  <Options
                    options={steps[currentStep].options}
                    currentStep={currentStep}
                    handleOptionClick={handleOptionClick}
                  />
                )}

              {steps[currentStep]?.stepButtons && (
                <StepButtons buttons={steps[currentStep].stepButtons} />
              )}
            </div>

            <div className="privacy-policy">
              *Ao continuar você estará de acordo com nossa{" "}
              <a
                href="https://www.mycon.com.br/politica-de-privacidade"
                target="_blank"
                rel="noopener noreferrer"
              >
                <u>Política de Privacidade</u>
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatBot;
